import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import type { StatefulDiaryT } from "@/types/database/diaries";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useMemo } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { TrendBadge } from "./TrendBadge";

dayjs.extend(isSameOrAfter);

interface TotalDiariesCardProps {
	diaries: StatefulDiaryT[];
}

export function TotalDiariesCard({ diaries }: TotalDiariesCardProps) {
	const now = dayjs();
	// Get the start of the current week (Monday)
	const startOfCurrentWeek = now.startOf("week").add(1, "day");
	// Get the start of the previous week (Monday)
	const startOfPreviousWeek = startOfCurrentWeek.subtract(1, "week");
	// Get the end of the previous week (Sunday)
	const endOfPreviousWeek = startOfCurrentWeek.subtract(1, "day");

	// Count diaries from the previous week
	const previousWeekDiaries = diaries.filter((diary) => {
		const diaryDate = dayjs(diary.start_date);
		return (
			diaryDate.isSameOrAfter(startOfPreviousWeek) &&
			diaryDate.isBefore(startOfCurrentWeek)
		);
	}).length;

	// Prepare data for the stacked area chart of diary status
	const stackedDiaryStatusData = useMemo(() => {
		if (!diaries.length) return [];

		// Sort diaries by start date
		const sortedDiaries = [...diaries].sort(
			(a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf(),
		);

		// Get the first diary date and today
		const firstDate = dayjs(sortedDiaries[0]?.start_date);
		const today = dayjs();

		// Create a map of dates to counts for each status
		const dateMap = new Map<
			string,
			{ active: number; pending: number; inactive: number }
		>();

		// Initialize all dates between first diary and today
		let currentDate = firstDate;
		while (currentDate.isBefore(today) || currentDate.isSame(today, "day")) {
			const dateStr = currentDate.format("YYYY-MM-DD");
			dateMap.set(dateStr, { active: 0, pending: 0, inactive: 0 });
			currentDate = currentDate.add(1, "day");
		}

		// Fill in the counts for each diary - make it cumulative
		let activeCount = 0;
		let pendingCount = 0;
		let inactiveCount = 0;

		for (const diary of sortedDiaries) {
			const diaryDate = dayjs(diary.start_date).format("YYYY-MM-DD");

			// Update the cumulative counts
			if (diary.tab === "active") {
				activeCount++;
			} else if (diary.tab === "pending") {
				pendingCount++;
			} else if (diary.tab === "inactive") {
				inactiveCount++;
			}

			// Update all dates from this diary's start date onwards
			for (const [dateStr, counts] of dateMap.entries()) {
				if (dayjs(dateStr).isSameOrAfter(diaryDate)) {
					counts.active = activeCount;
					counts.pending = pendingCount;
					counts.inactive = inactiveCount;
				}
			}
		}

		// Convert map to array of objects for the chart
		return Array.from(dateMap.entries())
			.map(([date, counts]) => ({
				date,
				...counts,
			}))
			.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
	}, [diaries]);

	return (
		<Card className="w-full md:w-1/3">
			<CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
				<CardTitle className="font-medium text-sm">
					Arthrosamid Diaries
				</CardTitle>
				<div className="flex items-center gap-1 text-xs">
					<TrendBadge value={previousWeekDiaries} />
				</div>
			</CardHeader>
			<CardContent>
				{stackedDiaryStatusData.length > 0 ? (
					<div>
						<div className="flex justify-center gap-4 mt-1 mb-2">
							<div className="flex items-center gap-1">
								<div className="bg-[hsl(var(--chart-5))] rounded-full w-2.5 h-2.5" />
								<span className="text-slate-600 text-xs">Active</span>
							</div>
							<div className="flex items-center gap-1">
								<div className="bg-[hsl(var(--chart-2))] rounded-full w-2.5 h-2.5" />
								<span className="text-slate-600 text-xs">Pending</span>
							</div>
							<div className="flex items-center gap-1">
								<div className="bg-[hsl(var(--chart-4))] rounded-full w-2.5 h-2.5" />
								<span className="text-slate-600 text-xs">Inactive</span>
							</div>
						</div>
						<ChartContainer
							className="w-full h-[120px]"
							config={{
								active: {
									label: "Active",
									color: "hsl(var(--chart-5))",
								},
								pending: {
									label: "Pending",
									color: "hsl(var(--chart-2))",
								},
								inactive: {
									label: "Incomplete",
									color: "hsl(var(--chart-4))",
								},
							}}
						>
							<AreaChart
								data={stackedDiaryStatusData}
								margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
								height={100}
								key="diary-status-chart"
							>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis
									dataKey="date"
									tickFormatter={(value) => dayjs(value).format("DD MMM")}
									tickLine={false}
									axisLine={false}
									height={20}
									tick={{ fontSize: 10 }}
									interval="preserveStartEnd"
								/>
								<YAxis tickLine={false} axisLine={false} width={20} />
								<Tooltip
									content={({ active, payload, label }) => {
										if (active && payload && payload.length) {
											return (
												<div className="bg-white/80 shadow-sm backdrop-blur-sm p-2 border border-slate-200 rounded-md">
													<div className="mb-1 text-slate-500 text-xs">
														{dayjs(label).format("DD MMM YYYY")}
													</div>
													<div className="space-y-1">
														{payload.map((entry, index) => (
															<div
																key={`item-${entry.name}-${index}`}
																className="flex justify-between items-center gap-2"
															>
																<div className="flex items-center gap-1.5">
																	<div
																		className="rounded-full w-2.5 h-2.5"
																		style={{ backgroundColor: entry.color }}
																	/>
																	<span className="font-medium text-slate-700 text-xs capitalize">
																		{entry.name}
																	</span>
																</div>
																<span className="font-medium tabular-nums text-slate-900 text-xs">
																	{entry.value}
																</span>
															</div>
														))}
													</div>
												</div>
											);
										}
										return null;
									}}
								/>
								<Area
									type="monotone"
									dataKey="inactive"
									stackId="1"
									stroke="hsl(var(--chart-4))"
									fill="hsl(var(--chart-4))"
									fillOpacity={0.3}
									strokeWidth={1.5}
								/>
								<Area
									type="monotone"
									dataKey="pending"
									stackId="1"
									stroke="hsl(var(--chart-2))"
									fill="hsl(var(--chart-2))"
									fillOpacity={0.3}
									strokeWidth={1.5}
								/>
								<Area
									type="monotone"
									dataKey="active"
									stackId="1"
									stroke="hsl(var(--chart-5))"
									fill="hsl(var(--chart-5))"
									fillOpacity={0.3}
									strokeWidth={1.5}
								/>
							</AreaChart>
						</ChartContainer>
					</div>
				) : (
					<div className="flex justify-center items-center h-[120px] text-muted-foreground">
						No diary data available
					</div>
				)}
			</CardContent>
		</Card>
	);
}
